/**
 * InfraHostDetailPage (#670)
 *
 * Detail view for a single edge host. Tabs: Overview / Active Alerts / Logs.
 * Overview leads with an identity card and 4 lead-KPI tiles, then a
 * sparkline grid of secondary metrics.
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Badge, Button, Card, Spinner, Alert, EmptyState,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Sparkline,
  Heading, Text,
  IconButton,
} from '@agion/ui'
import {
  ArrowLeft, Server, Cpu, HardDrive, Activity,
  AlertTriangle, Clock, ExternalLink, MemoryStick, Gauge as GaugeIcon, Zap,
  RefreshCw, Hash, Globe2, TrendingUp, TrendingDown,
} from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import {
  listHosts,
  getHost,
  getHostMetrics,
  listActiveAlerts,
  MonitoringUnavailableError,
  retryMonitoringRequest,
} from './infra_monitoring.service'
import type { MonitoringHost, ActiveAlert, HostMetricSignal } from '@/core/infra_types'
import { formatRelativeTime, cn } from '@/core/infra_utils'
import { deriveAlertName, deriveAlertMessage } from './alert_helpers'
import { AlertRcaSection } from './AlertRcaSection'
import { HostStatusBadge, SeverityBadge, SeverityRibbon, SectionTitle } from './MonitoringPrimitives'

// ============= Metric Helpers =============

function last(arr: number[]): number | null {
  if (arr.length === 0) return null
  const val = arr[arr.length - 1]
  return val !== undefined ? val : null
}

function trend(arr: number[]): { delta: number | null; direction: 'up' | 'down' | 'flat' } {
  if (arr.length < 2) return { delta: null, direction: 'flat' }
  const a = arr[arr.length - 2]
  const b = arr[arr.length - 1]
  if (a === undefined || b === undefined) return { delta: null, direction: 'flat' }
  const delta = b - a
  if (Math.abs(delta) < 0.05) return { delta, direction: 'flat' }
  return { delta, direction: delta > 0 ? 'up' : 'down' }
}

function formatValue(val: number | null, isInteger?: boolean): string {
  if (val === null) return '—'
  return isInteger ? val.toString() : val.toFixed(1)
}

function metricSignal(metrics: Awaited<ReturnType<typeof getHostMetrics>>, id: string): HostMetricSignal | undefined {
  return metrics.signals?.find((signal) => signal.id === id)
}

function metricValues(metrics: Awaited<ReturnType<typeof getHostMetrics>>, id: string, fallback: number[]): number[] {
  return metricSignal(metrics, id)?.values ?? fallback
}

function metricCurrent(
  metrics: Awaited<ReturnType<typeof getHostMetrics>>,
  id: string,
  fallback: number[],
  fallbackCurrent?: (values: number[]) => number | null,
): number | null {
  const signal = metricSignal(metrics, id)
  if (signal) {
    if (signal.status !== 'ok') return null
    return signal.current ?? last(signal.values)
  }
  return fallbackCurrent ? fallbackCurrent(fallback) : last(fallback)
}

function metricAvailabilityLabel(signal: HostMetricSignal | undefined, t: ReturnType<typeof useTranslation>['t']): string | undefined {
  if (!signal || signal.status === 'ok') return undefined
  switch (signal.unavailableReason ?? signal.status) {
    case 'query_error':
      return t('pages:monitoring.hostDetail.metricAvailability.queryError', 'Query error')
    case 'not_allowed':
      return t('pages:monitoring.hostDetail.metricAvailability.notAllowed', 'Not allowed')
    case 'not_applicable':
      return t('pages:monitoring.hostDetail.metricAvailability.notApplicable', 'Not applicable')
    case 'not_installed':
      return t('pages:monitoring.hostDetail.metricAvailability.notInstalled', 'Not installed')
    case 'stale':
      return t('pages:monitoring.hostDetail.metricAvailability.stale', 'Stale')
    case 'not_emitting':
    case 'no_data':
    default:
      return t('pages:monitoring.hostDetail.metricAvailability.noTelemetry', 'No telemetry')
  }
}

export function InfraHostDetailPage() {
  const { t } = useTranslation(['pages'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  const { data: host, isLoading: hostLoading, error: hostError } = useQuery({
    queryKey: ['monitoring', 'host', id],
    queryFn: () => getHost(id!),
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: !!id,
  })

  const fallbackEnabled = host === null || hostError instanceof MonitoringUnavailableError
  const { data: hosts = [], isLoading: hostsLoading, error: hostsError } = useQuery({
    queryKey: ['monitoring', 'hosts'],
    queryFn: listHosts,
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: !!id && fallbackEnabled,
  })

  const resolvedHost = useMemo(() => {
    if (host) return host
    return hosts.find((h) => h.id === id) ?? null
  }, [host, hosts, id])

  const { data: metrics, isLoading: metricsLoading, error: metricsError, isFetching: metricsFetching, refetch: refetchMetrics } = useQuery({
    queryKey: ['monitoring', 'metrics', id],
    queryFn: () => getHostMetrics(id!),
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: !!id,
  })

  const { data: alerts = [], isLoading: alertsLoading, error: alertsError } = useQuery({
    queryKey: ['monitoring', 'alerts', { hostId: id }],
    queryFn: () => listActiveAlerts({ hostId: id }),
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: !!id,
  })

  const isLoading = hostLoading || (fallbackEnabled && hostsLoading)

  const loadError = hostError ?? hostsError
  if (loadError && !(loadError instanceof MonitoringUnavailableError)) {
    return (
      <div className="container-standard">
        <Alert variant="error">
          {loadError instanceof Error
            ? loadError.message
            : t('pages:monitoring.hosts.loadError', 'Failed to load host data')}
        </Alert>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="container-standard">
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      </div>
    )
  }

  if (hostError instanceof MonitoringUnavailableError && hostsError instanceof MonitoringUnavailableError) {
    return (
      <div className="container-standard">
        <Alert variant="info">
          {t('pages:monitoring.endpoint_not_deployed', 'Unable to load host details.')}
        </Alert>
      </div>
    )
  }

  if (!resolvedHost) {
    return (
      <div className="container-standard">
        <EmptyState
          icon={Server}
          title={t('pages:monitoring.hosts.notFound', 'Host not found')}
          description={t('pages:monitoring.hosts.notFoundDescription', 'It may have been removed or the ID is incorrect.')}
          action={
            <Button variant="secondary" onClick={() => navigate('/hosts')}>
              {t('pages:monitoring.hosts.backToHosts', 'Back to Hosts')}
            </Button>
          }
        />
      </div>
    )
  }

  const metricsUnavailable = metricsError instanceof MonitoringUnavailableError

  return (
    <div className="container-standard">
      <PageHeader
        icon={Server}
        iconColor="text-blue-600"
        title={resolvedHost.name}
        description={resolvedHost.environmentName}
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t('pages:monitoring.hostDetail.back', 'Back')}
              onClick={() => navigate(-1)}
              icon={<ArrowLeft size={16} />}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetchMetrics()}
              disabled={metricsFetching}
              className="flex items-center gap-2"
            >
              <RefreshCw size={14} className={metricsFetching ? 'animate-spin' : ''} />
              {t('pages:monitoring.hostDetail.refresh', 'Refresh')}
            </Button>
          </div>
        }
      />

      {metricsUnavailable && (
        <Alert variant="info" className="mb-6">
          {t('pages:monitoring.hostDetail.metricsUnavailable', 'Host metrics unavailable. Refresh to try again.')}
        </Alert>
      )}

      {/* Identity card */}
      <IdentityCard host={resolvedHost} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="overview">
            {t('pages:monitoring.hostDetail.tabs.overview', 'Overview')}
          </TabsTrigger>
          <TabsTrigger value="alerts">
            {t('pages:monitoring.hostDetail.tabs.alerts', 'Active Alerts')}
            {alerts.length > 0 && (
              <Badge variant="error" className="ml-2 tabular-nums">{alerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="logs">
            {t('pages:monitoring.hostDetail.tabs.logs', 'Logs')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            metrics={metrics}
            metricsError={metricsError as Error | null | undefined}
            metricsLoading={metricsLoading}
          />
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          <AlertsTab alerts={alerts} isLoading={alertsLoading} error={alertsError as Error | null | undefined} />
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <LogsTab hostId={id!} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

interface IdentityCardProps {
  host: MonitoringHost
}

function IdentityCard({ host }: IdentityCardProps) {
  const { t } = useTranslation(['pages'])
  return (
    <Card className="overflow-hidden">
      <div className="grid gap-6 p-5 md:grid-cols-[auto,1fr,auto] md:items-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <Server className="h-7 w-7" aria-hidden="true" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <IdentityField
            icon={Globe2}
            label={t('pages:monitoring.hostDetail.environment', 'Environment')}
            value={host.environmentName}
          />
          <IdentityField
            icon={Clock}
            label={t('pages:monitoring.hostDetail.lastHeartbeat', 'Last Heartbeat')}
            value={formatRelativeTime(host.lastHeartbeat)}
            title={new Date(host.lastHeartbeat).toLocaleString()}
          />
          <IdentityField
            icon={AlertTriangle}
            label={t('pages:monitoring.hostDetail.firingAlerts', 'Firing Alerts')}
            value={host.firingAlerts.length}
            tone={host.firingAlerts.length > 0 ? 'critical' : 'neutral'}
          />
          <IdentityField
            icon={Hash}
            label={t('pages:monitoring.hostDetail.hostId', 'Host ID')}
            value={<span className="font-mono text-xs">{host.id}</span>}
          />
        </div>
        <div>
          <HostStatusBadge
            status={host.status}
            label={t(`pages:monitoring.hosts.status.${host.status}`, host.status)}
          />
        </div>
      </div>
    </Card>
  )
}

type Tone = 'neutral' | 'critical'
const TONE_FIELD: Record<Tone, string> = {
  neutral:  'text-slate-900 dark:text-slate-100',
  critical: 'text-rose-700 dark:text-rose-300',
}

function IdentityField({
  icon: Icon, label, value, title, tone = 'neutral',
}: {
  icon: typeof Server
  label: string
  value: React.ReactNode
  title?: string
  tone?: Tone
}) {
  return (
    <div className="min-w-0" title={title}>
      <div className="mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        <Text size="xs" color="muted" className="uppercase tracking-wider">
          {label}
        </Text>
      </div>
      <div className={cn('truncate text-sm font-medium', TONE_FIELD[tone])}>
        {value}
      </div>
    </div>
  )
}

interface OverviewTabProps {
  metrics?: Awaited<ReturnType<typeof getHostMetrics>>
  metricsError?: Error | null
  metricsLoading?: boolean
}

function OverviewTab({ metrics, metricsError, metricsLoading }: OverviewTabProps) {
  const { t } = useTranslation(['pages'])

  if (metricsError && !(metricsError instanceof MonitoringUnavailableError)) {
    return (
      <Alert variant="error">
        {metricsError.message ?? t('pages:monitoring.hostDetail.metricsError', 'Failed to load metrics')}
      </Alert>
    )
  }

  if (metricsLoading && !metrics) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (!metrics) {
    return (
      <EmptyState
        icon={Activity}
        title={t('pages:monitoring.hostDetail.noMetricsTitle', 'No metrics available')}
        description={t('pages:monitoring.hostDetail.noMetrics', 'No metrics available')}
      />
    )
  }

  type LeadItem = {
    key: string
    label: string
    icon: typeof Server
    data: number[]
    unit: string
    current: number | null
    threshold: { warn: number; crit: number }
    inverse?: boolean
    isInteger?: boolean
    color: string
    availabilityLabel?: string
  }
  type SecondaryItem = {
    key: string
    label: string
    icon: typeof Server
    data: number[]
    unit: string
    current: number | null
    color: string
    availabilityLabel?: string
  }
  const cpuSignal = metricSignal(metrics, 'cpu')
  const memSignal = metricSignal(metrics, 'mem_available')
  const diskSignal = metricSignal(metrics, 'disk_used_percent')
  const oomSignal = metricSignal(metrics, 'oom_kills')
  const ioWaitSignal = metricSignal(metrics, 'io_wait')
  const swapSignal = metricSignal(metrics, 'swap')
  const errorRateSignal = metricSignal(metrics, 'agion_error_rate')

  const cpuValues = metricValues(metrics, 'cpu', metrics.cpu)
  const memValues = metricValues(metrics, 'mem_available', metrics.memAvailable)
  const diskValues = metricValues(metrics, 'disk_used_percent', metrics.diskUsedPercent)
  const oomValues = metricValues(metrics, 'oom_kills', metrics.oomKills)
  const ioWaitValues = metricValues(metrics, 'io_wait', metrics.ioWait)
  const swapValues = metricValues(metrics, 'swap', metrics.swap)
  const errorRateValues = metricValues(metrics, 'agion_error_rate', metrics.agionErrorRate)

  const lead: LeadItem[] = [
    { key: 'cpu', label: t('pages:monitoring.hostDetail.metrics.cpu', 'CPU'), icon: Cpu, data: cpuValues, unit: '%', current: metricCurrent(metrics, 'cpu', metrics.cpu), threshold: { warn: 70, crit: 90 }, color: 'var(--chart-1)', availabilityLabel: metricAvailabilityLabel(cpuSignal, t) },
    { key: 'memAvailable', label: t('pages:monitoring.hostDetail.metrics.memAvailable', 'Memory Available'), icon: MemoryStick, data: memValues, unit: '%', current: metricCurrent(metrics, 'mem_available', metrics.memAvailable), threshold: { warn: 30, crit: 10 }, inverse: true, color: 'var(--chart-2)', availabilityLabel: metricAvailabilityLabel(memSignal, t) },
    { key: 'diskUsed', label: t('pages:monitoring.hostDetail.metrics.diskUsed', 'Disk Used'), icon: HardDrive, data: diskValues, unit: '%', current: metricCurrent(metrics, 'disk_used_percent', metrics.diskUsedPercent), threshold: { warn: 75, crit: 90 }, color: 'var(--chart-3)', availabilityLabel: metricAvailabilityLabel(diskSignal, t) },
    { key: 'oom-kills', label: t('pages:monitoring.hostDetail.metrics.oomKills', 'OOM Kills'), icon: AlertTriangle, data: oomValues, unit: '', current: metricCurrent(metrics, 'oom_kills', metrics.oomKills, (values) => values.length === 0 ? null : values.reduce((a, b) => a + b, 0)), isInteger: true, threshold: { warn: 1, crit: 3 }, color: 'var(--chart-5)', availabilityLabel: metricAvailabilityLabel(oomSignal, t) },
  ]

  const secondary: SecondaryItem[] = [
    { key: 'io-wait', label: t('pages:monitoring.hostDetail.metrics.ioWait', 'I/O Wait'), icon: Activity, data: ioWaitValues, unit: '%', current: metricCurrent(metrics, 'io_wait', metrics.ioWait), color: 'var(--chart-4)', availabilityLabel: metricAvailabilityLabel(ioWaitSignal, t) },
    { key: 'swap', label: t('pages:monitoring.hostDetail.metrics.swap', 'Swap'), icon: GaugeIcon, data: swapValues, unit: '%', current: metricCurrent(metrics, 'swap', metrics.swap), color: 'var(--chart-1)', availabilityLabel: metricAvailabilityLabel(swapSignal, t) },
    { key: 'error-rate', label: t('pages:monitoring.hostDetail.metrics.errorRate', 'Error Rate'), icon: Zap, data: errorRateValues, unit: '/min', current: metricCurrent(metrics, 'agion_error_rate', metrics.agionErrorRate), color: 'var(--error)', availabilityLabel: metricAvailabilityLabel(errorRateSignal, t) },
  ]

  return (
    <div className="space-y-8">
      {/* Lead KPIs */}
      <section>
        <SectionTitle hint={<Text size="xs" color="muted">{t('pages:monitoring.hostDetail.last24h', 'last 24h')}</Text>}>
          {t('pages:monitoring.hostDetail.headlineMetrics', 'Headline metrics')}
        </SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {lead.map((item) => {
            const tone = thresholdTone(item.current, item.threshold, item.inverse)
            const tr = trend(item.data)
            return (
              <KpiTile
                key={item.key}
                testId={`metric-${item.key}`}
                label={item.label}
                icon={item.icon}
                value={formatValue(item.current, item.isInteger)}
                unit={item.unit}
                data={item.data}
                color={item.color}
                tone={tone}
                trend={tr}
                availabilityLabel={item.availabilityLabel}
              />
            )
          })}
        </div>
      </section>

      {/* Secondary KPIs */}
      <section>
        <SectionTitle>
          {t('pages:monitoring.hostDetail.secondaryMetrics', 'Other signals')}
        </SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {secondary.map((item) => (
            <KpiTile
              key={item.key}
              testId={`metric-${item.key}`}
              label={item.label}
              icon={item.icon}
              value={formatValue(item.current)}
              unit={item.unit}
              data={item.data}
              color={item.color}
              tone="neutral"
              availabilityLabel={item.availabilityLabel}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

type KpiTone = 'neutral' | 'success' | 'warning' | 'critical'

const KPI_RING: Record<KpiTone, string> = {
  neutral:  'ring-slate-200/70 dark:ring-slate-700/60',
  success:  'ring-emerald-300/60 dark:ring-emerald-500/30',
  warning:  'ring-amber-300/60 dark:ring-amber-500/30',
  critical: 'ring-rose-300/60 dark:ring-rose-500/30',
}

const KPI_VALUE: Record<KpiTone, string> = {
  neutral:  'text-slate-900 dark:text-slate-50',
  success:  'text-emerald-700 dark:text-emerald-300',
  warning:  'text-amber-700 dark:text-amber-300',
  critical: 'text-rose-700 dark:text-rose-300',
}

function thresholdTone(
  value: number | null,
  threshold: { warn: number; crit: number },
  inverse?: boolean,
): KpiTone {
  if (value === null) return 'neutral'
  if (inverse) {
    if (value <= threshold.crit) return 'critical'
    if (value <= threshold.warn) return 'warning'
    return 'success'
  }
  if (value >= threshold.crit) return 'critical'
  if (value >= threshold.warn) return 'warning'
  return 'success'
}

function TrendIndicator({ direction, delta }: { direction: 'up' | 'down' | 'flat'; delta: number }) {
  const { t } = useTranslation(['pages'])
  const Icon = direction === 'up' ? TrendingUp : TrendingDown
  const ariaLabel = direction === 'up'
    ? t('pages:monitoring.hostDetail.trendRising', 'rising')
    : t('pages:monitoring.hostDetail.trendFalling', 'falling')
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium tabular-nums',
        direction === 'up' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400',
      )}
      aria-label={ariaLabel}
    >
      <Icon size={12} aria-hidden="true" />
      {Math.abs(delta).toFixed(1)}
    </span>
  )
}

function KpiTile({
  testId, label, icon: Icon, value, unit, data, color, tone, trend: tr, availabilityLabel,
}: {
  testId: string
  label: string
  icon: typeof Server
  value: string
  unit: string
  data: number[]
  color: string
  tone: KpiTone
  trend?: { delta: number | null; direction: 'up' | 'down' | 'flat' }
  availabilityLabel?: string
}) {
  const displayValue = availabilityLabel ?? value
  return (
    <Card className={cn('relative overflow-hidden ring-1 ring-inset', KPI_RING[tone])}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon size={14} className="text-slate-400 dark:text-slate-500" aria-hidden="true" />
            <Text size="xs" color="muted" className="uppercase tracking-wider truncate">
              {label}
            </Text>
          </div>
          {tr && tr.direction !== 'flat' && tr.delta !== null && <TrendIndicator direction={tr.direction} delta={tr.delta} />}
        </div>
        <div className="mt-2 flex items-end justify-between gap-2">
          <div
            className={cn(
              availabilityLabel
                ? 'text-sm font-medium leading-5 text-slate-500 dark:text-slate-400'
                : 'text-3xl font-semibold tabular-nums leading-none',
              availabilityLabel ? '' : KPI_VALUE[tone],
            )}
            data-testid={testId}
          >
            {displayValue}
            {!availabilityLabel && unit && <span className="ml-1 text-sm font-normal text-slate-400 dark:text-slate-500">{unit}</span>}
          </div>
          <Sparkline
            data={data}
            width={96}
            height={32}
            color={color}
          />
        </div>
      </div>
    </Card>
  )
}

interface AlertsTabProps {
  alerts: ActiveAlert[]
  isLoading: boolean
  error?: Error | null
}

function AlertsTab({ alerts, isLoading, error }: AlertsTabProps) {
  const { t } = useTranslation(['pages'])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error instanceof MonitoringUnavailableError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={t('pages:monitoring.hostDetail.alertsBackendPendingTitle', 'Alert data unavailable')}
        description={t('pages:monitoring.hostDetail.alertsBackendPending', 'Active alerts cannot be confirmed until monitoring backend is available.')}
      />
    )
  }

  if (error) {
    return (
      <Alert variant="error">
        {error.message ?? t('pages:monitoring.hostDetail.alertsError', 'Failed to load alerts')}
      </Alert>
    )
  }

  if (alerts.length === 0) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={t('pages:monitoring.hostDetail.noActiveAlertsTitle', 'No active alerts')}
        description={t('pages:monitoring.hostDetail.noActiveAlerts', 'No active alerts')}
      />
    )
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => (
        <AlertCard key={alert.fingerprint} alert={alert} />
      ))}
    </div>
  )
}

function AlertCard({ alert }: { alert: ActiveAlert }) {
  const { t } = useTranslation(['pages'])
  const alertName = deriveAlertName(alert)
  const alertMessage = deriveAlertMessage(alert)
  const severityLabel = t(`pages:monitoring.alerts.severity.${alert.severity}`, alert.severity)

  return (
    <SeverityRibbon severity={alert.severity}>
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <SeverityBadge
                severity={alert.severity}
                label={severityLabel}
                ariaLabel={t('pages:monitoring.alerts.severityAlert', '{{severity}} alert', { severity: severityLabel })}
                icon={AlertTriangle}
              />
              <Heading level="h4" className="truncate text-base">{alertName}</Heading>
            </div>
            {alertMessage && (
              <Text size="sm" color="muted" className="mt-1 truncate">{alertMessage}</Text>
            )}
          </div>
          <span
            className="flex-shrink-0 text-xs text-slate-400 dark:text-slate-500"
            title={new Date(alert.startsAt).toLocaleString()}
          >
            {formatRelativeTime(alert.startsAt)}
          </span>
        </div>

        <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">
          <AlertRcaSection fingerprint={alert.fingerprint} orgId={alert.orgId} />
        </div>
      </div>
    </SeverityRibbon>
  )
}

function LogsTab({ hostId }: { hostId: string }) {
  const { t } = useTranslation(['pages'])

  return (
    <Card>
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <ExternalLink className="h-5 w-5 text-slate-500 dark:text-slate-400" aria-hidden="true" />
        </div>
        <Heading level="h4" className="text-base">
          {t('pages:monitoring.hostDetail.logsTitle', 'Live systemd journal')}
        </Heading>
        <Text size="sm" color="muted" className="max-w-md">
          {t('pages:monitoring.hostDetail.logsDescription', 'View live systemd journal logs for this host.')}
        </Text>
        <Link to={`/agent-hosts/${hostId}/logs`}>
          <Button variant="primary" className="mt-2 flex items-center gap-2">
            <ExternalLink size={14} />
            {t('pages:monitoring.hostDetail.openLogs', 'Open Log Viewer')}
          </Button>
        </Link>
      </div>
    </Card>
  )
}
